import { MAX_HIGHEST_NUMERIC_INPUT, MIN_LOWEST_NUMERIC_INPUT } from './consts';

const receiverTypeValues = ['business', 'private'] as const;
export type ReceiverType = (typeof receiverTypeValues)[number];

const priceTypeValues = ['net', 'gross'] as const;
export type PriceType = (typeof priceTypeValues)[number];

const quoteStatusTypeValues = ['draft', 'sent', 'inReview', 'accepted', 'declined', 'ordered'];
export type QuoteStatusType = (typeof quoteStatusTypeValues)[number];

export interface ValidationField {
  field: string;
  value: string | number | boolean;
}

interface ValidationMessage {
  key: string;
  param?: Record<string, string | number>;
}

export const createQuoteValidationRules = {
  quote: {
    status: {
      type: quoteStatusTypeValues,
      required: true,
    },
    sender: {
      name: {
        maxLength: 50,
        required: false,
      },
      organizationNumber: {
        maxLength: 50,
        required: false,
      },
      reference: {
        maxLength: 100,
        required: false,
      },
      address: {
        address: {
          maxLength: 100,
          required: false,
        },
        postalCode: {
          maxLength: 50,
          required: false,
        },
        postalAddress: {
          maxLength: 100,
          required: false,
        },
      },
    },
    receiver: {
      type: {
        type: receiverTypeValues,
        required: true,
      },
      label: {
        maxLength: 50,
        required: false,
      },
      name: {
        maxLength: 100,
        required: false,
      },
      contactPerson: {
        maxLength: 100,
        required: false,
      },
      address: {
        address: {
          maxLength: 100,
          required: false,
        },
        postalCode: {
          maxLength: 50,
          required: false,
        },
        postalAddress: {
          maxLength: 100,
          required: false,
        },
      },
    },
    priceType: {
      type: priceTypeValues,
      required: true,
    },
    mergeArticles: {
      required: true,
    },
    showDiscounts: {
      required: true,
    },
    finalMessage: {
      maxLength: 500,
      required: false,
    },
    validFrom: {
      required: false,
    },
    validFor: {
      maxValue: MAX_HIGHEST_NUMERIC_INPUT,
      maxLength: 6,
      required: false,
    },
    quoteItem: {
      quantity: {
        minValue: 0,
        maxValue: MAX_HIGHEST_NUMERIC_INPUT,
        maxLength: 6,
        required: true,
      },
      markup: {
        minValue: -100,
        maxValue: MAX_HIGHEST_NUMERIC_INPUT,
        // 999999 %
        maxLength: 9,
        required: true,
      },
      salesDiscount: {
        minValue: MIN_LOWEST_NUMERIC_INPUT,
        maxValue: 100,
        // 99.99% but 99999 is validated and block autosave
        maxLength: 5,
        required: true,
      },
      netPrice: {
        minValue: 0,
        maxValue: MAX_HIGHEST_NUMERIC_INPUT,
        required: true,
      },
      salesPrice: {
        minValue: 0,
        maxValue: MAX_HIGHEST_NUMERIC_INPUT,
        required: true,
      },
      quoteNetPrice: {
        minValue: 0,
        maxValue: MAX_HIGHEST_NUMERIC_INPUT,
        maxLength: 9,
        required: true,
      },
      quoteSalesPrice: {
        minValue: 0,
        maxValue: MAX_HIGHEST_NUMERIC_INPUT,
        maxLength: 9,
        required: true,
      },
    },
    quoteCustomItem: {
      customItemName: {
        maxLength: 100,
        required: false,
      },
      quantity: {
        minValue: 0,
        maxValue: MAX_HIGHEST_NUMERIC_INPUT,
        maxLength: 6,
        required: false,
      },
      unit: {
        maxLength: 10,
        required: false,
      },
      vatRate: {
        minValue: 0,
        maxValue: MAX_HIGHEST_NUMERIC_INPUT,
        maxLength: 2,
        required: false,
      },
      price: {
        minValue: 0,
        maxValue: MAX_HIGHEST_NUMERIC_INPUT,
        maxLength: 9,
        required: false,
      },
      isRot: {
        required: true,
      },
    },
  },
};

export const createNestedObject = (path: string[], value: any): Record<string, any> => {
  return path.reduceRight((acc, key) => ({ [key]: acc }), value);
};

const getNestedRule = (rule: any, path: string): any => {
  return path.split('.').reduce((acc, key) => acc?.[key], rule);
};

export const validateCreateQuoteField = (validationField: ValidationField): ValidationMessage | null => {
  const value = validationField.value;
  const field = validationField.field;

  const rule = getNestedRule(createQuoteValidationRules, field);
  const { required, maxLength, minLength, maxValue, minValue, type } = rule;

  if (required && (value === null || value === '')) {
    return { key: 'errors.input_errors.value_missing' };
  }

  if (typeof value === 'string' && value.length > maxLength) {
    return {
      key: 'validation.string.max',
      param: { limit: maxLength },
    };
  }

  if (typeof value === 'string' && value.length < minLength) {
    return {
      key: 'validation.string.min',
      param: { limit: minLength },
    };
  }

  if (typeof value === 'number' && value > maxValue) {
    return {
      key: 'validation.number.max',
      param: { limit: maxValue },
    };
  }

  if (typeof value === 'number' && value < minValue) {
    return {
      key: 'validation.number.min',
      param: { limit: minValue },
    };
  }

  if (Array.isArray(type) && !type.includes(value)) {
    return { key: 'validation.enum.invalid_value' };
  }

  return null;
};
